"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FaArrowLeft, FaDownload, FaPrint } from "react-icons/fa";
import { openCertificatePdf } from "../../lib/certificates";

export default function CertificatePdf({ session }) {
  const { certificateId } = useParams();
  const location = useLocation();
  const backToManagement = new URLSearchParams(location.search).get("from") === "management";
  const [pdfUrl, setPdfUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const frameRef = useRef(null);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    async function loadPdf() {
      setLoading(true);
      setError("");
      try {
        if (!certificateId || !session?.access_token) throw new Error("Certificate session is unavailable.");
        const response = await fetch(`/api/certificates?action=pdf&id=${encodeURIComponent(certificateId)}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!response.ok) {
          let message = "Unable to retrieve certificate PDF.";
          try { message = (await response.json())?.error || message; } catch {}
          throw new Error(message);
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (active) setPdfUrl(objectUrl);
      } catch (loadError) {
        if (active) setError(loadError?.message || "Unable to open certificate PDF.");
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadPdf();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [certificateId, session?.access_token]);

  const download = async () => {
    try {
      await openCertificatePdf({ certificate: { id: certificateId }, session, download: true });
    } catch (downloadError) {
      setError(downloadError?.message || "Unable to download certificate PDF.");
    }
  };

  const print = () => {
    try { frameRef.current?.contentWindow?.print(); }
    catch { window.print(); }
  };

  return (
    <main className="ruht-certificate-pdf-page" id="main-content">
      <header className="ruht-certificate-pdf-toolbar">
        <Link to={backToManagement ? "/intranet/management/certificates" : "/intranet/certificates"} className="ruht-certificate-pdf-toolbar__back"><FaArrowLeft /> {backToManagement ? "Certificate management" : "My certificates"}</Link>
        <div className="ruht-certificate-pdf-toolbar__title">
          <strong>RUHT certificate</strong>
          <span>Secure staff certificate record</span>
        </div>
        <div className="ruht-certificate-pdf-toolbar__actions">
          <button type="button" onClick={download} disabled={!pdfUrl}><FaDownload /> Download</button>
          <button type="button" onClick={print} disabled={!pdfUrl}><FaPrint /> Print</button>
        </div>
      </header>

      <section className="ruht-certificate-pdf-stage">
        {loading && <div className="ruht-certificate-pdf-message">Opening certificate PDF…</div>}
        {error && <div className="ruht-certificate-pdf-message is-error"><strong>Unable to open certificate</strong><p>{error}</p></div>}
        {pdfUrl && !error && <iframe ref={frameRef} src={pdfUrl} title="RUHT certificate PDF" className="ruht-certificate-pdf-frame" />}
      </section>
    </main>
  );
}
